import { useEffect, useState } from "react";
import { formatLocalDateTime } from "../../../utils/formatDate";
import FuncionarioService from "../../../services/resource/funcionarioService";
import FaltaService from "../../../services/resource/FaltaService";
import { useSearchParams } from "react-router-dom";

// Mapa de ícone/cor por motivo
const MOTIVO_STYLE = {
    "Férias": { color: "bg-blue-100 text-blue-700", icon: "fa-umbrella-beach" },
    "Atestado": { color: "bg-amber-100 text-amber-700", icon: "fa-file-medical" },
    "Falta": { color: "bg-red-100 text-red-700", icon: "fa-calendar-times" },
};
const motivoStyle = (motivo) =>
    MOTIVO_STYLE[motivo] ?? { color: "bg-gray-100 text-gray-600", icon: "fa-info-circle" };

function TabelaAfastamentosPhone() {
    const [searchParams] = useSearchParams();

    const ano = Number(searchParams.get("ano"));
    const mes = Number(searchParams.get("mes"));
    const funcionarioId = searchParams.get("funcionarioId");
    const tipoDistribuidor = searchParams.get("tipoDistribuidor");
    const funcionarioCodigo = searchParams.get("funcionarioCodigo");

    const funcionarioService = new FuncionarioService();
    const faltaService = new FaltaService();

    const [afastamentos, setAfastamentos] = useState([]);
    const [loading, setLoading] = useState(true);

    const values = {
        funcionarioId: funcionarioId,
        ano,
        mes,
        tipoDistribuidor: tipoDistribuidor,
        funcionarioCodigo: funcionarioCodigo,
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([
            funcionarioService.listarAfastamentos(values),
            faltaService.listarAfastamentos(values),
        ])
            .then(([resFuncionarios, resFaltas]) => {
                setAfastamentos([...resFuncionarios.data, ...resFaltas.data]);
            })
            .finally(() => setLoading(false));
    }, []);

    const totalDias = afastamentos.reduce(
        (acc, a) => acc + (Number(a.diasAfastados) || 0), 0
    );

    return (
        <div className="min-h-screen flex flex-col gap-3 p-3">

            {/* ── Aviso Fortes RH ── */}
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <i className="fa fa-exclamation-triangle text-amber-500 text-base mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                    Os dados de <span className="font-semibold">férias, afastamentos e faltas</span> são
                    integrados diretamente ao <span className="font-semibold">Fortes RH</span>.
                    Caso haja inconsistências, solicite o ajuste ao{" "}
                    <span className="font-semibold">setor de RH</span> e informe a equipe para
                    que as <span className="font-semibold">comissões sejam recalculadas</span>.
                </p>
            </div>

            {/* ── Estado de carregamento ── */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-spinner fa-spin text-2xl text-teal-500" />
                    <p className="text-xs">Carregando afastamentos...</p>
                </div>
            )}

            {/* ── Sem registros ── */}
            {!loading && afastamentos.length === 0 && (
                <div className="flex flex-col items-center justify-center py-14 gap-2 text-gray-400">
                    <i className="fa fa-calendar-check text-4xl opacity-20" />
                    <p className="text-xs">Nenhum afastamento encontrado no período.</p>
                </div>
            )}

            {/* ── Cards ── */}
            {!loading && afastamentos.length > 0 && (
                <>
                    <div className="space-y-2">
                        {afastamentos.map((a, i) => {
                            const { color, icon } = motivoStyle(a.motivo);
                            return (
                                <div
                                    key={a.id ?? i}
                                    className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-start gap-3"
                                >
                                    {/* Ícone do motivo */}
                                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm ${color}`}>
                                        <i className={`fa ${icon}`} />
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        {/* Cabeçalho */}
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div>
                                                <p className="text-sm font-semibold text-gray-800 leading-tight">
                                                    {a.funcionarioNome ?? "—"}
                                                </p>
                                                <p className="text-[10px] text-gray-400 mt-0.5">
                                                    Código: {a.funcionarioCodigo ?? "—"}
                                                </p>
                                            </div>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${color}`}>
                                                {a.motivo ?? "—"}
                                            </span>
                                        </div>

                                        {/* Datas e dias */}
                                        <div className="grid grid-cols-3 gap-2 mt-2.5">
                                            <MiniInfo label="Início" value={formatLocalDateTime(a.dataInicial)} />
                                            <MiniInfo label="Fim" value={formatLocalDateTime(a.dataFinal)} />
                                            <MiniInfo
                                                label="Dias afastados"
                                                value={a.diasAfastados ?? "—"}
                                                bold
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* ── Rodapé totais ── */}
                    <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 flex justify-between items-center">
                        <span className="text-xs text-gray-500">
                            {afastamentos.length} registro{afastamentos.length !== 1 ? "s" : ""}
                        </span>
                        <span className="text-xs font-bold text-gray-800">
                            Total: <span className="text-red-600">{totalDias} dia{totalDias !== 1 ? "s" : ""}</span> afastados
                        </span>
                    </div>
                </>
            )}
        </div>
    );
}

export default TabelaAfastamentosPhone;

function MiniInfo({ label, value, bold }) {
    return (
        <div>
            <p className="text-[9px] text-gray-400 uppercase">{label}</p>
            <p className={`text-xs leading-tight ${bold ? "font-bold text-gray-900" : "font-medium text-gray-700"}`}>
                {value ?? "—"}
            </p>
        </div>
    );
}
